import { useEffect, useState } from "react";
import { Dropdown, Form, Spinner } from "react-bootstrap";
import ImageLoader from "../../../Common/ImageLoader";
import { getAPIData } from "../../../../Utils/commonApi";
import { useAuth } from "../../../context/Auth";

const headers = ['Conflicted Products', 'Category', 'Subcategory', 'Published'];

const StorefrontTab = ({ productList, categoryList, storefrontState, setStorefrontState }) => {
  const { authTokens } = useAuth();

  const handleProductChange = (id, updatedItem) => {
    setStorefrontState(prev => prev.map(item => item.id === id ? { ...item, ...updatedItem } : item));
  }

  async function getSubcategoriesById(id) {
    return getAPIData(1079, authTokens, {
      optionEnum1: id
    })
  }

  useEffect(() => {
    if (!productList) return;
    const newState = productList.map((item) => ({
      ...item,
      id: item.ProductId,
      productIcon: item.ProductIcon,
      productName: item.ProductName,
      category: item.CategoryName,
      categoryId: item.CategoryId,
      subcategory: item.SubCategoryName,
      subcategoryId: item.SubCategoryId,
      published: item.Published
    }));
    setStorefrontState(newState);
  }, [productList]);

  return (
    <div className="storefront-tab-wrapper overflow-x-auto d-grid">
      <div className="storefront-tab no-wrap hide-scrollbar ">
        {storefrontState?.length > 0 ? (
          <>
            <div className="storefront-tab-header">
              {headers.map((header) => (
                <div key={header} className="header-item f-darkgrey">
                  {header}
                </div>
              ))}
            </div>
            <div className="storefront-tab-body">
              {storefrontState.map((item) => (
                <ProductItem 
                  key={item.id} 
                  item={item} 
                  categoryList={categoryList} 
                  handleProductChange={handleProductChange} 
                  getSubcategoriesById={getSubcategoriesById} 
                />
              ))}
            </div>
          </>
        ) : (
          <div className="storefront-tab-content-item d-flex align-items-center gap-16 p-4">
            <span className="storefront-tab-content-item-label">No products</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default StorefrontTab;

const ProductItem = ({ item, handleProductChange, getSubcategoriesById, categoryList }) => {
  const [isSubcategoriesLoading, setIsSubcategoriesLoading] = useState(false);
  const [subcategoriesList, setSubcategoriesList] = useState([]);
  const categoryTitle = `${item.category} ${item.subcategory ? ` > ${item.subcategory}` : ''}`

  const productItemId = `product-item-${item.id}`;

  const onPublishedChange = (id, published) => {
    handleProductChange(id, { ...item, published });
  }

  const onCategoryChange = (id, category) => {
    handleProductChange(id, 
      { 
        ...item, 
        category: category.Name,
        categoryId: category.ObjectId,
        subcategory: undefined,
        subcategoryId: undefined
      }
    );
    setSubcategoriesList([]);
    document.getElementById(productItemId)?.click();
  }

  const onSubcategoryChange = (id, subcategory) => {
    handleProductChange(id, { 
      ...item, 
      subcategory: subcategory.Name,
      subcategoryId: subcategory.ObjectId
    });
    document.getElementById(productItemId)?.click();
  }
  
  return (
    <div id={productItemId} key={item.productName} className="storefront-tab-body-item">
      <div>
        <div className="d-flex align-items-center gap-8">
          <ImageLoader 
            src={item.productIcon} 
            alt="" 
            width={24} 
            height={24}
            noImageComponent={(
              <img src="/images/settings-nav/product_icon.svg" alt="" width={24} height={24} />
            )}
          />
          <div>
            <h3 className="m-0 f-14 line-height-22 f-black f-500 normal-wrap">{item.productName}</h3>
            <p className="m-0 f-12 line-height-18 f-darkgrey f-400 normal-wrap">{categoryTitle}</p>
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center w-100">
        <Dropdown className="transparennt-wrapper-dropdown my-dropdown my-dropdown-common w-100">
          <Dropdown.Toggle className="d-flex align-items-center justify-content-between gap-4 toggle-wrapper w-100 text-ellipsis">
            <span className="f-14 line-height-22 f-grey f-400 text-ellipsis">
              {item?.category}
            </span>
            <div className="arrow-icon" />
          </Dropdown.Toggle>
          <Dropdown.Menu renderOnMount className="menu hide-scrollbar" popperConfig={{ strategy: 'fixed' }}>
            {categoryList?.map(category => (
              <div className="item" key={category.ObjectId} onClick={() => onCategoryChange(item.id, category)}>
                {category.Name}
              </div>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="d-flex align-items-center w-100">
        <Dropdown 
          className="transparennt-wrapper-dropdown my-dropdown my-dropdown-common w-100"
          onToggle={(isOpen) => {
            if (!isOpen) return;
            if (subcategoriesList?.length || isSubcategoriesLoading) return;
            setIsSubcategoriesLoading(true);
            getSubcategoriesById(item.categoryId).then(res => {
              setSubcategoriesList(res?.SubCategory_List);
            }).finally(() => {
              setIsSubcategoriesLoading(false);
            });
          }}
        >
          <Dropdown.Toggle className="d-flex align-items-center justify-content-between gap-4 toggle-wrapper w-100 text-ellipsis">
            <span className="f-14 line-height-22 f-grey f-400 text-ellipsis">
              {item?.subcategory ? item?.subcategory : 'Select'}
            </span>
            <div className="arrow-icon" />
          </Dropdown.Toggle>
          <Dropdown.Menu renderOnMount className="menu hide-scrollbar" popperConfig={{ strategy: 'fixed' }}>
            {isSubcategoriesLoading && (
              <div className="p-2 d-flex align-items-center gap-8">
                <Spinner 
                  animation="border"
                  variant="secondary"
                  size="sm"
                  style={{ width: '16px', height: '16px' }}
                />
                <span className="visually-hidden">Loading...</span>
              </div>
            )}
            {subcategoriesList?.map(subcategory => (
              <div className="item" key={subcategory.ObjectId} onClick={() => onSubcategoryChange(item.id, subcategory)}>
                {subcategory.Name}
              </div>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="d-flex align-items-center justify-content-center w-100">
        <Form.Check 
          id={`published-${item.id}`}
          className="blue-checkbox fix-checkbox checkboxstyle" 
          type="checkbox" 
          checked={item.published}
          onClick={(e) => {
            onPublishedChange(item.id, !item.published);
          }}
        />
      </div>
    </div>
  )
}
